/**
 * Connection callback.
 *
 * The handoff's central warning governs this whole page: *"The query string or a
 * successful redirect alone is not proof of connection."* So nothing here shows
 * "Connected" because the page loaded. A connection is established only when
 * `POST …/complete` answers `active`.
 *
 * What this page does, in order:
 *
 *   1. Resolves the signed-in Merko user and requires it to be the **same** user
 *      that started the attempt. Two users sharing a browser must not be able to
 *      complete each other's connections.
 *   2. Ties the callback to the attempt held in session state. Composio's
 *      redirect carries only `session_uri`, so the attempt normally comes from
 *      that stored state alone; if the URL also carries an `attempt_id` it must
 *      equal the stored one. No stored attempt, a mismatch, or an expired
 *      attempt stops the flow dead.
 *   3. Sends the opaque `session_uri` for OAuth, or `{}` for an API-key link.
 *   4. On `pending`, polls the stored attempt and reconciles with `{}` until the
 *      completion says `active`, the attempt fails, or it expires.
 *   5. Retries only with `{}`, never by replaying the single-use URI — a
 *      transient failure may already have redeemed it.
 *
 * It accepts no owner ID, account ID, auth config ID or toolkit from the query
 * string. Those are the server's business, and taking them from the browser is
 * exactly the confusion this page exists to prevent.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { Page, PageHeader } from "components/ui";
import { Button } from "components/ui/button";
import {
    attemptTimeLeftMs,
    clearAttempt,
    describeFailure,
    matchAttempt,
    useAttempt,
    useCompleteAttempt,
    useMerkoUserKey,
    useRefreshCatalog,
} from "core/features/integrations";
import type { StoredAttempt } from "core/features/integrations";

type Phase =
    | { kind: "checking" }
    | { kind: "completing"; attempt: StoredAttempt }
    | { kind: "pending"; attempt: StoredAttempt }
    | { kind: "active" }
    | { kind: "blocked"; title: string; detail: string }
    | { kind: "failed"; attempt: StoredAttempt; detail: string };

/** Stored-attempt states that say the provider side is done and worth reconciling. */
const SETTLED_ATTEMPT = ["verified", "completed"];
/** Never reconcile more often than this, however fast the attempt is polled. */
const RECONCILE_EVERY_MS = 4_000;

const EXPIRED = {
    title: "This connection attempt expired",
    detail: "Connection attempts are valid for ten minutes. Please start again.",
};

export function ConnectCallback() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const userKey = useMerkoUserKey();
    const complete = useCompleteAttempt();
    const refreshCatalog = useRefreshCatalog();

    const [phase, setPhase] = useState<Phase>({ kind: "checking" });
    /* The callback must be acted on once. A re-render that completed again
       would replay a single-use URI, which is the one thing to avoid. */
    const started = useRef(false);
    const inFlight = useRef(false);
    const lastReconcileAt = useRef(0);

    /**
     * Only these two are read from the query string. Nothing else is trusted.
     *
     * `session_uri` is Composio's own parameter name, and the only thing its
     * redirect adds. `attempt_id` is optional: present only if Merko's own
     * callback URL includes it.
     */
    const callbackAttemptId = params.get("attempt_id");
    const sessionUri = params.get("session_uri");

    const liveAttempt =
        phase.kind === "completing" || phase.kind === "pending" || phase.kind === "failed"
            ? phase.attempt
            : null;
    const attemptState = useAttempt(liveAttempt?.attemptId ?? null, {
        poll: phase.kind === "pending",
    });

    const block = useCallback((title: string, detail: string) => {
        clearAttempt();
        setPhase({ kind: "blocked", title, detail });
    }, []);

    /**
     * Ask the server to complete the attempt.
     *
     * `uri` is the single-use session URI and is passed exactly once, on the
     * first call. Every later call — a manual retry or a pending reconcile —
     * passes null and so sends `{}`.
     */
    const runCompletion = useCallback(
        async (attempt: StoredAttempt, uri: string | null, quiet = false) => {
            if (inFlight.current) return;
            inFlight.current = true;
            lastReconcileAt.current = Date.now();
            if (!quiet) setPhase({ kind: "completing", attempt });

            try {
                const result = await complete.mutateAsync({
                    attemptId: attempt.attemptId,
                    sessionUri: uri ?? undefined,
                });

                if (result.status === "active") {
                    clearAttempt();
                    refreshCatalog();
                    setPhase({ kind: "active" });
                    return;
                }
                // A 200 that says pending is not a connection yet.
                setPhase({ kind: "pending", attempt });
            } catch (error) {
                const failure = describeFailure(error);

                if (failure.treatment === "abort_callback") {
                    block("This connection could not be verified", failure.message);
                } else if (failure.code === "already_connected") {
                    // Refresh the card rather than making a second account.
                    clearAttempt();
                    refreshCatalog();
                    setPhase({ kind: "active" });
                } else if (failure.treatment === "restart_connect") {
                    block("This attempt is no longer valid", failure.message);
                } else if (failure.treatment === "refresh" || failure.unavailable) {
                    block("This connection can’t be completed", failure.message);
                } else if (!quiet) {
                    // Transient. A retry is offered — with `{}`, never the URI.
                    setPhase({ kind: "failed", attempt, detail: failure.message });
                }
                // A quiet reconcile that failed transiently just waits for the next tick.
            } finally {
                inFlight.current = false;
            }
        },
        [block, complete, refreshCatalog],
    );

    // ── first pass: who is this, and is it the attempt they started? ───────
    useEffect(() => {
        if (started.current) return;
        // Still resolving the session; the comparison is meaningless without it.
        if (userKey === undefined) return;
        started.current = true;

        if (userKey === null) {
            block(
                "You’re not signed in",
                "Sign in to Merko with the account that started this connection, then start it again.",
            );
            return;
        }

        const match = matchAttempt(userKey, callbackAttemptId);

        /* Narrowed with `in`, not `!match.ok`: this project compiles with
           `strict: false`, and without strictNullChecks a boolean discriminant
           does not narrow the union. */
        if ("reason" in match) {
            const copy = {
                missing: {
                    title: "No connection in progress",
                    detail:
                        "This browser has no record of starting this connection. If you began it elsewhere, or in another window, start it again from the Integrations page.",
                },
                mismatch: {
                    title: "This callback doesn’t match",
                    detail:
                        "The connection that came back is not the one this session started. For safety nothing has been connected — please start again.",
                },
                expired: EXPIRED,
            }[match.reason];
            block(copy.title, copy.detail);
            return;
        }

        void runCompletion(match.attempt, sessionUri);
    }, [block, callbackAttemptId, runCompletion, sessionUri, userKey]);

    // ── while pending: follow the stored attempt and reconcile with `{}` ───
    useEffect(() => {
        if (phase.kind !== "pending") return;
        const status = attemptState.data?.status;

        if (status === "failed") {
            block(
                "The provider could not complete this connection",
                "Nothing was connected. Please start again from the Integrations page.",
            );
            return;
        }

        /* The stored attempt says the provider side is done. That is still not
           proof of a live connection — only the completion response is — so
           ask again, with `{}`, and let that answer decide. */
        if (
            status &&
            SETTLED_ATTEMPT.includes(status) &&
            Date.now() - lastReconcileAt.current >= RECONCILE_EVERY_MS
        ) {
            void runCompletion(phase.attempt, null, true);
        }
    }, [attemptState.data?.status, attemptState.dataUpdatedAt, block, phase, runCompletion]);

    // ── an attempt that outlives its ten minutes needs a new connect flow ──
    useEffect(() => {
        if (!liveAttempt) return;
        const timer = setTimeout(
            () => block(EXPIRED.title, EXPIRED.detail),
            attemptTimeLeftMs(liveAttempt),
        );
        return () => clearTimeout(timer);
    }, [block, liveAttempt]);

    const goToCatalog = () => navigate("/integrations", { replace: true });

    return (
        <Page width="narrow">
            <PageHeader title="Finishing the connection" />

            {/* A live region, so each change of state is announced. */}
            <div
                role="status"
                aria-live="polite"
                className="mx-auto max-w-md rounded-xl border border-line bg-surface p-6 text-center"
            >
                {(phase.kind === "checking" || phase.kind === "completing") && (
                    <>
                        <Loader2
                            className="mx-auto h-8 w-8 animate-spin text-content-muted motion-reduce:animate-none"
                            aria-hidden="true"
                        />
                        <h2 className="mt-3 text-ui font-semibold text-content">
                            Verifying this connection…
                        </h2>
                        <p className="mt-1 text-meta text-content-muted">
                            Confirming it with Merko. Nothing is connected until this finishes.
                        </p>
                    </>
                )}

                {phase.kind === "pending" && (
                    <>
                        <Loader2
                            className="mx-auto h-8 w-8 animate-spin text-content-muted motion-reduce:animate-none"
                            aria-hidden="true"
                        />
                        <h2 className="mt-3 text-ui font-semibold text-content">
                            Still finishing up
                        </h2>
                        <p className="mt-1 text-meta text-content-muted">
                            The provider accepted the connection but it is not active yet. This page
                            keeps checking — you can leave it open.
                        </p>
                        <Button variant="ghost" className="mt-4" onClick={goToCatalog}>
                            Back to integrations
                        </Button>
                    </>
                )}

                {phase.kind === "active" && (
                    <>
                        <CheckCircle2
                            className="mx-auto h-8 w-8 text-emerald-600 dark:text-emerald-400"
                            aria-hidden="true"
                        />
                        <h2 className="mt-3 text-ui font-semibold text-content">Connected</h2>
                        <p className="mt-1 text-meta text-content-muted">
                            You can set up a contact import from the Integrations page.
                        </p>
                        <Button variant="primary" className="mt-4" onClick={goToCatalog}>
                            Go to integrations
                        </Button>
                    </>
                )}

                {phase.kind === "failed" && (
                    <>
                        <AlertTriangle
                            className="mx-auto h-8 w-8 text-amber-600 dark:text-amber-400"
                            aria-hidden="true"
                        />
                        <h2 className="mt-3 text-ui font-semibold text-content">
                            Couldn’t finish the connection
                        </h2>
                        <p className="mt-1 text-meta text-content-muted">{phase.detail}</p>
                        <div className="mt-4 flex items-center justify-center gap-2">
                            {/* Retries never resend the one-use URI. */}
                            <Button
                                variant="primary"
                                loading={complete.isPending}
                                onClick={() => void runCompletion(phase.attempt, null)}
                            >
                                Try again
                            </Button>
                            <Button variant="ghost" onClick={goToCatalog}>
                                Back to integrations
                            </Button>
                        </div>
                        <p className="mt-3 text-meta text-content-muted">
                            If this keeps failing until the attempt expires, you will be asked to
                            start the connection again.
                        </p>
                    </>
                )}

                {phase.kind === "blocked" && (
                    <>
                        <ShieldAlert
                            className="mx-auto h-8 w-8 text-red-600 dark:text-red-400"
                            aria-hidden="true"
                        />
                        <h2 className="mt-3 text-ui font-semibold text-content">{phase.title}</h2>
                        <p className="mt-1 text-meta text-content-muted">{phase.detail}</p>
                        <Button variant="primary" className="mt-4" onClick={goToCatalog}>
                            Start again
                        </Button>
                    </>
                )}
            </div>
        </Page>
    );
}

export default ConnectCallback;
